import React, { useEffect, useState } from "react";
import { api, fmtNum } from "@finance/lib/api";
import { Button } from "@finance/components/ui/button";

const B = ["0-30", "31-60", "61-90", "90+"];

// Inventory aging by receipt date (AVCO value), slow-mover flag, printable.
export function InventoryAging() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/inventory/aging").then((r) => setD(r.data)).catch(() => setD({ rows: [], totals: {} })); }, []);
  if (!d) return null;
  return (
    <div className="mx-8 mb-8 rounded-sm border border-gray-200 bg-white" data-testid="inventory-aging">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-5 py-3">
        <div className="font-display text-base font-semibold">Inventory Aging (by receipt date)</div>
        <div className="flex items-center gap-4 text-sm">
          <span data-testid="inventory-total-value">Total value: <b className="lx-num">TZS {fmtNum(d.total_value || 0)}</b></span>
          <span data-testid="inventory-slow-value" className="text-amber-700">90+ days: <b className="lx-num">TZS {fmtNum(d.slow_moving_value || 0)}</b></span>
          <Button size="sm" variant="outline" data-testid="inventory-aging-pdf" onClick={() => window.print()}>Print / PDF</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
            <th className="px-4 py-2 text-left">Item</th><th className="px-4 py-2 text-right">On hand</th><th className="px-4 py-2 text-right">Value (TZS)</th>
            {B.map((b) => <th key={b} className="px-4 py-2 text-right">{b} days</th>)}
          </tr></thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.sku} className="border-b border-gray-100" data-testid={`aging-row-${r.sku}`}>
                <td className="px-4 py-2">{r.description} <span className="text-xs text-gray-400">{r.sku}</span>{r.slow_mover && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">SLOW</span>}</td>
                <td className="px-4 py-2 text-right lx-num">{fmtNum(r.on_hand)}</td>
                <td className="px-4 py-2 text-right lx-num">TZS {fmtNum(r.value)}</td>
                {B.map((b) => <td key={b} className="px-4 py-2 text-right lx-num">{r[b].qty ? `${fmtNum(r[b].qty)} · TZS ${fmtNum(r[b].value)}` : "—"}</td>)}
              </tr>
            ))}
            {!d.rows.length && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">No stock on hand.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Service Margin: revenue vs cost per service line, client roll-up, below-cost flags.
export function ServiceMargin() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ date_from: "", date_to: "", party: "" });
  const load = () => api.get("/reports/service-margin", { params: { date_from: f.date_from || undefined, date_to: f.date_to || undefined, party: f.party || undefined } }).then((r) => setD(r.data)).catch(() => setD({ rows: [], by_client: [], below_cost: [] }));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const inp = "h-8 rounded-sm border border-gray-300 px-2 text-sm";
  return (
    <div className="mx-8 mb-8 rounded-sm border border-gray-200 bg-white" data-testid="service-margin">
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-5 py-3">
        <div className="mr-auto font-display text-base font-semibold">Service Margin</div>
        <input type="date" className={inp} data-testid="sm-from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
        <input type="date" className={inp} data-testid="sm-to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
        <input className={inp} placeholder="Client" data-testid="sm-party" value={f.party} onChange={(e) => setF({ ...f, party: e.target.value })} />
        <Button size="sm" variant="outline" data-testid="sm-apply" onClick={load}>Apply</Button>
      </div>
      {d.below_cost.length > 0 && <div className="bg-red-50 px-5 py-2 text-sm text-red-700" data-testid="sm-below-cost">{d.below_cost.length} service line(s) sold below cost.</div>}
      <table className="w-full text-sm">
        <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200"><th className="px-4 py-2 text-left">PO</th><th className="px-4 py-2 text-left">Client</th><th className="px-4 py-2 text-left">Service</th><th className="px-4 py-2 text-right">Revenue</th><th className="px-4 py-2 text-right">Cost</th><th className="px-4 py-2 text-right">Margin</th></tr></thead>
        <tbody>
          {d.rows.map((r, i) => (
            <tr key={i} className={`border-b border-gray-100 ${r.below_cost ? "bg-red-50/60" : ""}`}>
              <td className="px-4 py-2">{r.po}</td><td className="px-4 py-2">{r.client}</td><td className="px-4 py-2">{r.description}</td>
              <td className="px-4 py-2 text-right lx-num">TZS {fmtNum(r.revenue)}</td><td className="px-4 py-2 text-right lx-num">TZS {fmtNum(r.cost)}</td>
              <td className={`px-4 py-2 text-right lx-num ${r.margin < 0 ? "text-red-600" : ""}`}>TZS {fmtNum(r.margin)}</td>
            </tr>
          ))}
          {d.by_client.map((c) => (
            <tr key={`c-${c.client}`} className="bg-gray-50 font-semibold"><td className="px-4 py-2" colSpan={3}>Total · {c.client}</td><td className="px-4 py-2 text-right lx-num">TZS {fmtNum(c.revenue)}</td><td className="px-4 py-2 text-right lx-num">TZS {fmtNum(c.cost)}</td><td className="px-4 py-2 text-right lx-num">TZS {fmtNum(c.margin)}</td></tr>
          ))}
          {!d.rows.length && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No service lines yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
